import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import Typography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import OptimizelyBlock, {
  OptimizelyBlockProps,
} from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import classNames from "classnames";
import TertiaryButton from "../Button/TertiaryButton";
import PersonContactCard, { PersonContactCardProps, VariantType } from "./Card";
import styles from "./index.module.scss";

type PersonContactContainerEditableProps = {
  header?: string;
  intro?: string;
  link?: LinkData;
  backgroundColor?: "beige" | "white";
};

type PersonContactContainerOwnProps = {
  cards: PersonContactCardProps[];
  showImage: boolean;
  anchorId?: string;
  blockId?: string;
  inEditMode?: boolean;
};

type PersonContactContainerProps = PersonContactContainerEditableProps &
PersonContactContainerOwnProps;

const PersonContactContainer = ({
  header,
  intro,
  cards,
  showImage,
  backgroundColor,
  link,
  anchorId,
  blockId,
  inEditMode,
}: PersonContactContainerProps) => {
  const isSmall = useIsBreakpointOrBelow(ScreenSize.MEDIUM);
  let centeredHeading: boolean = true;
  let variant: VariantType = "small";
  if (cards.length === 1) {
    if (header && (intro || (link && link.href))) {
      centeredHeading = false;
    }
    variant = "large";
  }
  if (cards.length === 2) {
    variant = "medium";
  }
  if (cards.length > 2) {
    variant = "small";
  }

  const gridClassName = classNames(styles.grid, {
    [(styles as any)[`grid_${variant}`]]: variant,
    [styles.grid_small__3_card]: variant === "small" && cards.length === 3,
  });

  const gridNoImageClassName = classNames(styles.grid, {
    [(styles as any)[`grid_noimage_${variant}`]]: variant,
    [styles.grid_noimage_small__3_card]: variant === "small" && cards.length === 3,
  });
  return (
    <Container
      id={anchorId}
      size="full"
      pageGutter={0}
      component="section"
      backgroundColor={backgroundColor === "beige" ? "ComplementaryBeigeTint03" : "White"}
    >
      <Container
        className={classNames({ [styles.left_heading]: !centeredHeading,
        })}>
        <Box
          width="full"
          className={styles.header_wrapper}
          alignItems={centeredHeading || isSmall ? "center" : "flex-start"}
          flexDirection="column"
          textAlign={centeredHeading || isSmall ? "center" : "left"}
          gap={4}
          mb={centeredHeading || isSmall ? [10, 12, 14] : 0}
        >
          {header && (
            <EpiProperty<PersonContactContainerEditableProps> name="header">
              <Typography

                variant="heading2"
                asElement="h2"
              >
                {header}
              </Typography>
            </EpiProperty>
          )}
          {intro && (
            <EpiProperty<PersonContactContainerEditableProps> name="intro">
              <Typography
                variant="intro">
                {intro}
              </Typography>
            </EpiProperty>
          )}
          {link && link.href && (
            <Box
              mt={4}>
              <EpiProperty<PersonContactContainerEditableProps> name="link">
                <TertiaryButton
                  label={link.text}
                  href={link.href}
                  target={link.target}
                  externalLinkText={link.externalLinkText}/>
              </EpiProperty>
            </Box>
          )}
        </Box>
        <Box className={showImage ? gridClassName : gridNoImageClassName}>
          {cards.map((card, i) => (
            <PersonContactCard
              color={backgroundColor === "beige" ? "white" : "beige" }
              key={i}
              showImage={showImage}
              image={card.image}
              name={card.name}
              title={card.title}
              variant={variant}
              department={card.department}
              phoneNumber={card.phoneNumber}
              phoneNumberFormatted={card.phoneNumberFormatted}
              emailLink={card.emailLink}
              contactPageLink={card.contactPageLink}
            />
          ))}
        </Box>
      </Container>
    </Container>
  );
};

export type ProvidedPersonContactContainerProps =
  OptimizelyBlockProps<PersonContactContainerEditableProps> &
  PersonContactContainerProps;

const ProvidedPersonContactContainer = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedPersonContactContainerProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <PersonContactContainer
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedPersonContactContainer;
